import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  getPublicDecks, 
  searchPublicDecks, 
  getPopularPublicDecks, 
  addPublicDeckToUser,
  isDeckInUserLibrary,
  Deck
} from '../services/deckService';
import { getUserInfo } from '../services/firebase';
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  InputAdornment,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import StarIcon from '@mui/icons-material/Star';
import PersonIcon from '@mui/icons-material/Person';
import CheckIcon from '@mui/icons-material/Check';

const PublicDecksPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [decks, setDecks] = useState<Deck[]>([]);
  const [popularDecks, setPopularDecks] = useState<Deck[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deckStatuses, setDeckStatuses] = useState<Record<string, {isOwn: boolean, isInLibrary: boolean}>>({});
  const [deckAuthors, setDeckAuthors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchDecks = async () => {
      setIsLoading(true);
      const popular = await getPopularPublicDecks(10);
      setPopularDecks(popular);
      
      let resultDecks: Deck[];
      if (searchTerm.trim()) {
        resultDecks = await searchPublicDecks(searchTerm);
      } else {
        resultDecks = await getPublicDecks();
      }
      setDecks(resultDecks);
      
      // Получаем информацию об авторах колод
      const allDecks = [...popular, ...resultDecks];
      const uniqueDecks = allDecks.filter(
        (deck, index, self) => index === self.findIndex(d => d.id === deck.id)
      );
      
      const authors: Record<string, string> = {};
      for (const deck of uniqueDecks) {
        if (!authors[deck.userId]) {
          const userInfo = await getUserInfo(deck.userId);
          authors[deck.userId] = userInfo?.displayName || t('decks.unknownUser');
        }
      }
      setDeckAuthors(authors);
      
      if (user) {
        // Проверяем статус каждой колоды для пользователя
        const statuses: Record<string, {isOwn: boolean, isInLibrary: boolean}> = {};
        for (const deck of uniqueDecks) {
          const isOwn = deck.userId === user.uid;
          const isInLibrary = isOwn ? true : await isDeckInUserLibrary(user.uid, deck.id);
          statuses[deck.id] = { isOwn, isInLibrary };
        }
        setDeckStatuses(statuses);
      }
      
      setIsLoading(false);
    };

    fetchDecks();
  }, [searchTerm, user, t]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    const results = await searchPublicDecks(searchTerm);
    setDecks(results);
    setTabValue(1); // Switch to search results tab
    
    setIsLoading(false);
  };

  const handleAddDeck = async (deckId: string, deckTitle: string) => {
    if (!user) {
      navigate('/signin');
      return;
    }
    
    // Проверяем, не является ли колода собственной или уже добавленной
    const status = deckStatuses[deckId] || { isOwn: false, isInLibrary: false };
    if (status.isOwn) {
      setSnackbar({
        open: true,
        message: t('decks.ownDeckMessage'),
        severity: 'info',
      });
      return;
    }
    
    if (status.isInLibrary) {
      setSnackbar({
        open: true,
        message: t('decks.alreadyInLibraryMessage'),
        severity: 'info',
      });
      return;
    }
    
    try {
      const success = await addPublicDeckToUser(user.uid, deckId);
      
      if (success) {
        // Обновляем статус колоды
        setDeckStatuses(prev => ({
          ...prev,
          [deckId]: { ...prev[deckId], isInLibrary: true }
        }));
        
        // Обновляем локальное значение популярности в обоих наборах колод
        const updateDeckPopularity = (deck: Deck) => {
          if (deck.id === deckId) {
            return {
              ...deck,
              stats: {
                ...deck.stats,
                popularity: (deck.stats.popularity || 0) + 1
              }
            };
          }
          return deck;
        };
        
        setDecks(prevDecks => prevDecks.map(updateDeckPopularity));
        setPopularDecks(prevDecks => prevDecks.map(updateDeckPopularity));
        
        setSnackbar({
          open: true,
          message: t('messages.deckAddedToLibrary'),
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: t('messages.failedToAddDeck'),
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error adding deck:', error);
      setSnackbar({
        open: true,
        message: t('messages.errorAddingDeck'),
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false,
    });
  };

  const renderDeckCard = (deck: Deck) => {
    const status = deckStatuses[deck.id] || { isOwn: false, isInLibrary: false };
    const authorName = status.isOwn ? t('decks.createdByYou') : deckAuthors[deck.userId] || t('decks.unknownUser');
    
    return (
      <Card 
        key={deck.id} 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          }
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography variant="h5" component="div" gutterBottom>
            {deck.title}
          </Typography>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {authorName}
            </Typography>
          </Box>
          <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon fontSize="small" sx={{ color: 'gold' }} />
            <Typography variant="body2">
              {deck.stats.popularity || 0}
            </Typography>
          </Box>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              minHeight: '40px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {deck.description}
          </Typography>
          
          <Typography variant="body2" color="text.secondary">
            {deck.stats.totalCards} {t('decks.cards')}
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {deck.tags.map((tag) => (
              <Chip key={tag} label={tag} size="small" />
            ))}
          </Box>
        </CardContent>
        <CardActions>
          <Button 
            size="small" 
            startIcon={<VisibilityIcon />}
            onClick={() => navigate(`/decks/${deck.id}`)}
          >
            {t('common.view')}
          </Button>
          {user ? (
            status.isOwn ? (
              <Button 
                size="small"
                color="primary"
                startIcon={<PersonIcon />}
                onClick={() => navigate('/decks')}
              >
                {t('decks.yourDeck')}
              </Button>
            ) : status.isInLibrary ? (
              <Button 
                size="small"
                color="success"
                startIcon={<CheckIcon />}
                onClick={() => navigate('/decks')}
              >
                {t('decks.inLibrary')}
              </Button>
            ) : (
              <Button
                size="small"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleAddDeck(deck.id, deck.title)}
              >
                {t('decks.addToLibrary')}
              </Button>
            )
          ) : (
            <Tooltip title={t('decks.signInToAdd')}>
              <span>
                <Button
                  size="small"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => navigate('/signin')}
                  disabled={!user}
                >
                  {t('decks.signInToAddButton')}
                </Button>
              </span>
            </Tooltip>
          )}
        </CardActions>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box sx={{ mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
          >
            {t('common.back')}
          </Button>
        </Box>
        
        <Typography variant="h4" sx={{ mb: 3 }}>{t('decks.publicDecks')}</Typography>

        <Paper sx={{ mb: 4, p: 2 }}>
          <form onSubmit={handleSearch}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t('decks.searchDecksPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <Button 
                      type="submit"
                      variant="contained"
                      disableElevation
                      disabled={isLoading}
                    >
                      {t('common.search')}
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </Paper>

        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{ mb: 3 }}
        >
          <Tab 
            icon={<StarIcon />} 
            label={t('decks.popularDecksShort')}
            iconPosition="start"
          />
          <Tab 
            icon={<SearchIcon />} 
            label={t('decks.searchResultsShort')}
            iconPosition="start"
          />
        </Tabs>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ mt: 3 }}>
            {tabValue === 0 && (
              <>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  <EmojiEventsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {t('decks.mostPopularDecks')}
                </Typography>
                
                {popularDecks.length === 0 ? (
                  <Paper sx={{ p: 3 }}>
                    <Typography>{t('decks.noPopularDecks')}</Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {popularDecks.map((deck) => (
                      <Box key={deck.id} sx={{ width: { xs: '100%', sm: '45%', md: '31%' } }}>
                        {renderDeckCard(deck)}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}

            {tabValue === 1 && (
              <>
                <Typography variant="h5" sx={{ mb: 2 }}>
                  <SearchIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {searchTerm 
                    ? t('decks.searchResults')
                    : t('decks.allPublicDecks')
                  }
                </Typography>
                
                {decks.length === 0 ? (
                  <Paper sx={{ p: 3 }}>
                    <Typography>
                      {searchTerm 
                        ? t('decks.noDecksMatching', { term: searchTerm })
                        : t('decks.noPublicDecksAvailable')
                      }
                    </Typography>
                  </Paper>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    {decks.map((deck) => (
                      <Box key={deck.id} sx={{ width: { xs: '100%', sm: '45%', md: '31%' } }}>
                        {renderDeckCard(deck)}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        )}
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PublicDecksPage; 